// File: src/admin/PaymentManagement.jsx
import React, { useState, useEffect } from 'react';

export default function PaymentManagement() {
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterRole, setFilterRole] = useState('all'); // 'all', 'candidate', 'business'

    const API_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

    useEffect(() => {
        fetchTransactions();
    }, []);

    const fetchTransactions = async () => {
        try {
            const token = localStorage.getItem('skillmatch_token');
            const res = await fetch(`${API_URL}/api/payments/admin/history`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (res.ok) {
                setTransactions(data);
            }
        } catch (error) {
            console.error("Lỗi tải giao dịch:", error);
        } finally {
            setLoading(false);
        }
    };

    // Helper format tiền tệ
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(amount);
    };

    // Helper format ngày
    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleString('vi-VN', {
            day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit'
        });
    };

    // Lọc dữ liệu theo tab
    const filteredData = transactions.filter(t => {
        if (!t.userId) return false; // Bỏ qua nếu user đã bị xóa
        if (filterRole === 'all') return true;
        return t.userId.role === filterRole;
    });

    // Tính tổng doanh thu hiển thị
    const totalRevenue = filteredData.reduce((sum, t) => sum + t.amount, 0);

    return (
        <div className="p-6 bg-gray-50 min-h-screen font-sans">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-2xl font-bold text-gray-900">Quản lý Doanh thu & Thanh toán</h1>
                    <p className="text-gray-500 text-sm mt-1">Theo dõi các gói dịch vụ đã được đăng ký.</p>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                        <p className="text-sm font-bold text-gray-400 uppercase">Tổng giao dịch</p>
                        <p className="text-3xl font-extrabold text-blue-600 mt-2">{filteredData.length}</p>
                    </div>
                    <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                        <p className="text-sm font-bold text-gray-400 uppercase">Doanh thu (Hiển thị)</p>
                        <p className="text-3xl font-extrabold text-green-600 mt-2">{formatCurrency(totalRevenue)}</p>
                    </div>
                </div>

                {/* Tabs Filter */}
                <div className="flex gap-4 mb-6 border-b border-gray-200 pb-1">
                    {['all', 'candidate', 'business'].map((role) => (
                        <button
                            key={role}
                            onClick={() => setFilterRole(role)}
                            className={`px-4 py-2 text-sm font-bold rounded-t-lg transition-colors ${
                                filterRole === role 
                                    ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50' 
                                    : 'text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            {role === 'all' ? 'Tất cả' : (role === 'candidate' ? 'Ứng viên' : 'Doanh nghiệp')}
                        </button>
                    ))}
                </div>

                {/* Table */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-gray-50 text-gray-500 text-xs uppercase border-b border-gray-200">
                                    <th className="p-4 font-bold">Khách hàng</th>
                                    <th className="p-4 font-bold">Vai trò</th>
                                    <th className="p-4 font-bold">Gói đăng ký</th>
                                    <th className="p-4 font-bold text-right">Số tiền</th>
                                    <th className="p-4 font-bold">Mã đơn</th>
                                    <th className="p-4 font-bold">Ngày thanh toán</th>
                                </tr>
                            </thead>
                            <tbody className="text-sm">
                                {loading ? (
                                    <tr>
                                        <td colSpan="6" className="p-8 text-center text-gray-500">Đang tải dữ liệu...</td>
                                    </tr>
                                ) : filteredData.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" className="p-8 text-center text-gray-500">Chưa có giao dịch nào.</td>
                                    </tr>
                                ) : (
                                    filteredData.map((t) => (
                                        <tr key={t._id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                                            <td className="p-4">
                                                <div className="font-bold text-gray-900">
                                                    {t.userId.role === 'business' 
                                                        ? (t.userId.profile?.companyName || "Chưa cập nhật")
                                                        : (t.userId.profile?.fullName || "Chưa cập nhật")}
                                                </div>
                                                <div className="text-xs text-gray-500">{t.userId.email}</div>
                                            </td>
                                            <td className="p-4">
                                                <span className={`px-2 py-1 rounded text-xs font-bold ${
                                                    t.userId.role === 'candidate' 
                                                        ? 'bg-blue-100 text-blue-700' 
                                                        : 'bg-purple-100 text-purple-700'
                                                }`}>
                                                    {t.userId.role === 'candidate' ? 'Ứng viên' : 'Doanh nghiệp'}
                                                </span>
                                            </td>
                                            <td className="p-4">
                                                <span className="font-bold text-gray-800 uppercase">{t.plan.replace('_', ' ')}</span>
                                            </td>
                                            <td className="p-4 text-right font-bold text-green-600">
                                                {formatCurrency(t.amount)}
                                            </td>
                                            <td className="p-4 text-gray-500 font-mono text-xs">
                                                #{t.orderCode}
                                            </td>
                                            <td className="p-4 text-gray-500">
                                                {formatDate(t.createdAt)}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}